import { fetchOcrJob } from "./ocrJobApi";
import { getMaxWaitMs, getPollIntervalMs } from "./llmExtractionConfig";

const TERMINAL_STATUSES = new Set(["completed", "failed"]);

const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }

    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });

/**
 * @param {string} jobId
 * @param {{ signal?: AbortSignal, onStatus?: (status: string, payload?: object) => void }} [options]
 */
export const pollOcrJob = async (jobId, options = {}) => {
  const startedAt = Date.now();
  const baseInterval = getPollIntervalMs();
  const maxWait = getMaxWaitMs();
  let interval = baseInterval;

  while (true) {
    if (options.signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }

    const elapsed = Date.now() - startedAt;
    if (elapsed >= maxWait) {
      const error = new Error(
        "Document extraction is still processing. Please wait and try again.",
      );
      error.code = "POLL_TIMEOUT";
      throw error;
    }

    let payload;
    try {
      payload = await fetchOcrJob(jobId);
    } catch (err) {
      if (options.signal?.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }
      await sleep(Math.min(interval, maxWait - elapsed), options.signal);
      interval = Math.min(interval + 1000, 8000);
      continue;
    }

    const status = payload?.status;
    options.onStatus?.(status, payload);

    if (status === "failed") {
      const error = new Error(
        payload?.message ||
          "We could not extract document details. Please enter them manually.",
      );
      error.code = "JOB_FAILED";
      throw error;
    }

    if (status === "completed") {
      return payload;
    }

    if (payload?.stale) {
      options.onStatus?.("stale", payload);
    }

    await sleep(Math.min(interval, maxWait - (Date.now() - startedAt)), options.signal);
    interval = Math.min(interval + 500, 8000);
  }
};
